import {
  AnalysisBatchCreateDto,
  AnalysisParams,
  AnalysisSampleParams,
  SignedUrlData,
} from '@/interfaces';
import { QueryKeyType } from '@/types';
import { AxiosResponse } from 'axios';
import { QueryFunctionContext } from '@tanstack/react-query';
import APIService from './base.service';

import { PDF_QC } from '@/enums';
import { RcFile } from 'antd/es/upload';
import UploadService from './upload.service';

const basePath = '/analysis-batch';

export class AnalysisService {
  static removeAnalysisBatch(id: string): Promise<AxiosResponse> {
    return APIService.delete(basePath + `/${id}`);
  }
  static getExportAnalysis(params: AnalysisParams): Promise<AxiosResponse> {
    const {
      dateOfCreationFrom,
      dateOfCreationTo,
      dateOfSendAnalysisFrom,
      dateOfSendAnalysisTo,
      ...res
    } = params;

    const newParams = {
      dateOfCreation: {
        startDate: dateOfCreationFrom,
        endDate: dateOfCreationTo,
      },
      dateOfSendAnalysis: {
        startDate: dateOfSendAnalysisFrom,
        endDate: dateOfSendAnalysisTo,
      },
      ...res,
    };
    return APIService.get(basePath + '/export-data', { params: newParams });
  }
  static restoreAnalysis(id: string): Promise<AxiosResponse> {
    return APIService.put(basePath + `/${id}/restore`);
  }

  static getAnalysisSamples(
    params: AnalysisSampleParams,
  ): Promise<AxiosResponse> {
    return APIService.get('analysis-sample', { params });
  }
  static updateStatusAnalysis(payload: {
    id: string;
    externalIds: string[];
    total?: number;
    isBypass?: boolean;
  }): Promise<
    AxiosResponse<
      {
        passed: string[];
        failed: Record<string, string[]>;
      },
      any
    >
  > {
    const { id, ...rest } = payload;
    return APIService.put(basePath + `/${id}/update-status`, rest);
  }

  static async uploadFileSamples(props: {
    displayId: string;
    files: RcFile[];
  }): Promise<void> {
    const { displayId, files } = props;
    const payload: {
      filename: string;
      type: string;
    }[] = files.map((file: RcFile) => ({
      filename: file.name,
      type: file.type,
    }));

    // debugger;
    const response = await APIService.post<SignedUrlData[]>(
      basePath + `/${displayId}/files/get-signed-url`,
      payload,
    );
    const presignedURLs = response?.data;
    await Promise.all(
      presignedURLs.map((signedUrlData, index: number) =>
        UploadService.putWithFormFileAsync(
          signedUrlData.uploadUrl,
          files[index],
          files[index].type,
        ),
      ),
    );
  }
  static createSample(props: {
    displayId: string;
    sessionId: string;
    externalIds: string[];
  }): Promise<any> {
    const { displayId, sessionId, externalIds } = props;

    return APIService.post(basePath + `/${displayId}/samples`, {
      sessionId,
      externalIds,
    });
  }
  static removeSample(props: {
    displayId: string;
    externalId: string;
  }): Promise<AxiosResponse> {
    const { displayId, externalId } = props;
    return APIService.delete(basePath + `/${displayId}/samples/${externalId}`);
  }
  static publishReport(id: string): Promise<AxiosResponse> {
    return APIService.patch(`analysis-sample/${id}/publish-report`);
  }

  static unpublishReport(id: string): Promise<AxiosResponse> {
    return APIService.patch(`analysis-sample/${id}/unpublish-report`);
  }

  static downloadAnalysisSampleReport(
    sampleId: string,
  ): Promise<AxiosResponse> {
    return APIService.get(
      `${basePath}/sample/${sampleId}/get-presigned-report`,
    );
  }

  static downloadAnalysisBatchReport(
    displayId: string,
    sampleIds: string[],
  ): Promise<AxiosResponse> {
    return APIService.get(`${basePath}/bulk-get-presigned-report`, {
      params: { displayId, ids: sampleIds },
    });
  }

  static resendReport(id: string): Promise<AxiosResponse> {
    return APIService.post('/analysis-sample/resend-report', { id });
  }

  static bulkResendReport(ids: string[]): Promise<AxiosResponse> {
    return APIService.post('/analysis-sample/bulk-resend-report', { ids });
  }

  static bulkPublishReport(ids: string[]): Promise<AxiosResponse> {
    return APIService.patch(`analysis-sample/bulk-publish-report`, { ids });
  }

  static bulkUnpublishReport(ids: string[]): Promise<AxiosResponse> {
    return APIService.patch(`analysis-sample/bulk-unpublish-report`, { ids });
  }

  static markUnderSupport(id: string): Promise<any> {
    return APIService.patch(`analysis-sample/${id}/mark-under-support-report`);
  }

  static markUnderSupportBulk(
    externalIds: string[],
    analysisBatchId?: string,
  ): Promise<any> {
    return APIService.patch(`analysis-sample/bulk-mark-under-support-report`, {
      externalIds,
      analysisBatchId,
    });
  }

  static syncAnalysisBatch(analysisBatchId: string): Promise<AxiosResponse> {
    return APIService.post(basePath + `/${analysisBatchId}/sync`);
  }

  static stopSyncAnalysisBatch(
    analysisBatchId?: string,
  ): Promise<AxiosResponse> {
    return APIService.delete(basePath + `/${analysisBatchId}/sync`);
  }

  static changePdfQCStatus(
    id: string,
    payload: {
      pdfQC: PDF_QC;
    },
  ): Promise<any> {
    return APIService.patch(`analysis-sample/${id}/update-pdf-qc-status`, {
      ...payload,
    });
  }

  static changePdfQCStatusBulk(
    externalIds: string[],
    analysisBatchId: string,
    payload: {
      pdfQC: PDF_QC;
    },
  ): Promise<any> {
    return APIService.patch(`analysis-sample/bulk-update-pdf-qc-status`, {
      externalIds,
      analysisBatchId,
      ...payload,
    });
  }

  static rerunAnalysis(id: string): Promise<any> {
    return APIService.post(`/analysis-batch/${id}/rerun`);
  }

  static getMlErrorMetaData(id: string): Promise<AxiosResponse> {
    return APIService.get(`/analysis-batch/${id}/ml-error-meta-data`);
  }

  // static updateKitProfileData(payload: {
  //   id: string;
  //   body: B2DProfileDataPayload;
  // }): Promise<AxiosResponse> {
  //   const { id, body } = payload;
  //   return APIService.patch(`analysis-sample/${id}/update-patient-name`, body);
  //   // return new Promise(resolve =>
  //   //   resolve({
  //   //     data: { data: 'hello world', name: 123 },
  //   //     status: 200,
  //   //     statusText: '',
  //   //     headers: '' as any,
  //   //     config: {} as any,
  //   //   }),
  //   // );
  // }

  static uploadMultiplePatients(payload: FormData): Promise<AxiosResponse> {
    return APIService.post(
      'analysis-sample/upload-patient-names-csv',
      payload,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  }

  static checkQcStatus(id: string) {
    return APIService.get(`analysis-batch/${id}/check-qc-status`);
  }
}
